import { match } from "ts-pattern"

import {
  IconClearAll,
  IconDotsVertical,
  IconFilter,
  IconFilterOff,
  IconPin,
  IconPinnedOff,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../DropdownMenu"
import { KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID } from "../DataTable"
import { BooleanFilter } from "../filters/BooleanFilter"
import { DateRangeFilter } from "../filters/DateRangeFilter"
import { MultiEnumFilter } from "../filters/MultiEnumFilter"
import { NumberRangeFilter } from "../filters/NumberRangeFilter"
import { SingleEnumFilter } from "../filters/SingleEnumFilter"
import { LE_TableInstance } from "../types"

export function ColumnDropdown<T extends Record<string, any> = object>({
  col,
  table,
}: {
  col: Column<T>
  table: LE_TableInstance<T>
}) {
  const state = table.getState()
  const selectedFilterValue = state.columnFilters?.find((f) => f.id === col.id)?.value

  if ([KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID].includes(col.id)) return null

  const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
  if (typeof label !== "string") {
    throw new Error(
      "column `header` must be a string, or `meta.textLabel` must be set.",
    )
  }

  const canSort = col.getCanSort()
  const canFilter =
    (col.getCanFilter() && !!col.columnDef.filterFn) ||
    // Force show filter in the kebab menu, for filterin in e.g. display columns
    col.columnDef.enableColumnFilter === true
  const canPin = col.getCanPin()
  const hasChildren = col.getFlatColumns().length > 1
  const canHide = col.getCanHide()

  if (col.columnDef.meta?.hideHeader) return null

  const shouldShow = (canFilter || canSort || canPin || canHide) && !hasChildren
  return shouldShow ? (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex">
        <span className="cursor-pointer">
          <IconDotsVertical size={14} className="" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-2">
        {canSort && (
          <>
            <DropdownMenuItem
              disabled={!col.getIsSorted()}
              onClick={() => col.clearSorting()}
            >
              <IconClearAll /> Clear sort
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={col.getIsSorted() === "asc"}
              onClick={() => col.toggleSorting(false)}
            >
              <IconSortAscending />
              Sort by {label} ascending
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={col.getIsSorted() === "desc"}
              onClick={() => col.toggleSorting(true)}
            >
              <IconSortDescending />
              Sort by {label} descending
            </DropdownMenuItem>
          </>
        )}

        {canFilter && canSort ? <DropdownMenuSeparator /> : null}

        {canFilter && (
          <>
            <DropdownMenuItem
              disabled={!selectedFilterValue}
              onClick={() => col.setFilterValue(undefined)}
            >
              <IconFilterOff /> Clear filter
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <IconFilter /> Filter
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {match(col.columnDef.filterFn)
                  .with("multiEnum", () => (
                    <MultiEnumFilter
                      col={col}
                      filterValue={selectedFilterValue as string[]}
                      table={table}
                    />
                  ))
                  .with("singleEnum", () => (
                    <SingleEnumFilter
                      col={col}
                      filterValue={selectedFilterValue as string}
                      table={table}
                    />
                  ))
                  .with("inNumberRange", () => (
                    <NumberRangeFilter
                      col={col}
                      filterValue={selectedFilterValue as [number, number, boolean]}
                    />
                  ))
                  .with("inDateRange", () => (
                    <DateRangeFilter
                      col={col}
                      filterValue={selectedFilterValue as [string, string, boolean]}
                    />
                  ))
                  .with("boolean", () => (
                    <BooleanFilter
                      col={col}
                      filterValue={selectedFilterValue as boolean}
                    />
                  ))
                  .otherwise(() => null)}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
        {canPin && (canFilter || canSort) ? <DropdownMenuSeparator /> : null}
        {canPin ? (
          <>
            {col.getIsPinned() ? (
              <DropdownMenuItem
                onClick={() => {
                  table.setColumnPinning((state) => ({
                    ...state,
                    left: state.left?.filter((id) => id !== col.id),
                  }))
                }}
              >
                <IconPinnedOff />
                Unpin column
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() =>
                  table.setColumnPinning({ left: [SELECTABLE_COLUMN_ID, col.id] })
                }
              >
                <IconPin />
                Pin column
              </DropdownMenuItem>
            )}
          </>
        ) : null}

        {canHide ? (
          <>
            <DropdownMenuItem
              onClick={() => {
                table.setColumnVisibility((state) => ({
                  ...state,
                  [col.id]: false,
                }))
              }}
            >
              <IconPinnedOff />
              Hide column
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null
}
